
















import { Editor as TipTapEditor, EditorContent } from '@tiptap/vue-2';
import debounce from 'debounce';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import Underline from '@tiptap/extension-underline';
import Superscript from '@tiptap/extension-superscript';
import Text from '@tiptap/extension-text';
import { columnResizingPluginKey } from 'prosemirror-tables';
import type { EditorView } from 'prosemirror-view';
import type { MergeValues, Field } from '@getaccept/editor-lib';
import {
  Colors,
  ProsemirrorType,
  ListItemNode,
  BulletListNode,
  HeadingNode,
  OrderedListNode,
  ParagraphNode,
  TableCellNode,
  TableNode,
  TableHeaderNode,
  TableRowNode,
  TableDocNode,
  MergeValueNode,
  HardBreakNode,
  TextAlign,
  EditorHelper,
} from '@getaccept/editor-lib';
import type { PropType, Ref } from '@vue/composition-api';
import {
  onBeforeUnmount,
  onMounted,
  computed,
  defineComponent,
  ref,
  watch,
} from '@vue/composition-api';
import type { TextContentDoc } from '@getaccept/lib-shared/src/types/text-content';
import { updateMergeValues, setContent, handleInit } from './helpers/prosemirror-viewer.helper';

export default defineComponent({
  name: 'ProsemirrorViewerTable',
  components: {
    EditorContent,
  },
  props: {
    borderColor: { type: String, default: '' },
    alternateRowColor: { type: String, default: '' },
    headerRowBackgroundColor: { type: String, default: '' },
    isPdfViewer: { type: Boolean, default: false },
    content: {
      type: [Object, String] as PropType<TextContentDoc | string>,
      default: '',
    },
    mergeValues: { type: Object as PropType<MergeValues>, required: true },
    fields: { type: Array as PropType<Field[]>, default: () => [] },
  },
  setup(props) {
    const viewer: Ref<TipTapEditor | null> = ref(null);
    const overflow = ref(false);
    const tableWrapper: Ref<HTMLElement> = ref(null);
    const editorContainer: Ref<HTMLElement> = ref(null);

    const setOverflow = () => {
      overflow.value = EditorHelper.hasHiddenContentRight(tableWrapper.value);
    };

    const debounceHandleScroll = debounce(setOverflow, 300);

    const handleMouseDown = ({ state }: EditorView) =>
      columnResizingPluginKey.getState(state).activeHandle !== -1;

    const handleLoaded = () => {
      handleInit(viewer.value, props.fields, props.mergeValues);

      tableWrapper.value = editorContainer.value?.querySelector('.tableWrapper');
      if (!tableWrapper.value) {
        return;
      }

      tableWrapper.value?.addEventListener('scroll', debounceHandleScroll);
      setOverflow();
    };

    const contentPayload = computed(() => ({
      viewer: viewer.value,
      content: props.content,
      fields: props.fields,
      mergeValues: props.mergeValues,
    }));

    const tableViewer = computed(() => ({
      content: props.content,
      editable: false,
      enableInputRules: false,
      enablePasteRules: false,
      extensions: [
        TableDocNode,
        ParagraphNode,
        Text,
        Bold,
        Italic,
        Strike,
        Superscript,
        Underline,
        OrderedListNode,
        BulletListNode,
        ListItemNode,
        MergeValueNode,
        Link.configure({
          autolink: false,
          openOnClick: true,
          linkOnPaste: false,
        }),
        TableNode.configure({ resizable: !props.isPdfViewer }),
        TableHeaderNode,
        TableCellNode,
        TableRowNode,
        HeadingNode,
        HardBreakNode,
        TextAlign.configure({
          types: [
            ProsemirrorType.Paragraph,
            ProsemirrorType.Heading,
            ProsemirrorType.BulletList,
            ProsemirrorType.OrderedList,
          ],
        }),
      ],
      onCreate: handleLoaded,
      editorProps: {
        handleDOMEvents: {
          mousedown: handleMouseDown,
        },
      },
    }));

    onMounted(() => {
      viewer.value = new TipTapEditor(tableViewer.value);
    });

    onBeforeUnmount(() => {
      tableWrapper.value?.removeEventListener('scroll', debounceHandleScroll);
      viewer.value.destroy();
    });

    watch(
      () => [props.fields, props.mergeValues],
      () => {
        updateMergeValues(contentPayload.value);
      }
    );

    watch(
      () => props.content,
      () => {
        setContent(contentPayload.value);
      }
    );

    return {
      viewer,
      Colors,
      editorContainer,
      handleMouseDown,
      setOverflow,
      handleLoaded,
      overflow,
      tableWrapper,
    };
  },
});
